/**
 * Afrikaans: Voice
 *
 * What this file covers: Phone numbers, calls, recordings and the phone assistant.
 *
 * Tone: Clear and reassuring. Recording and consent wording is legal in effect: translate it exactly.
 *
 * Afrikaans: Standard South African Afrikaans. Address the reader as "jy". Use the same Afrikaans word for the
 * same English term everywhere; menu.ts and common.ts set the words the other files follow. Afrikaans must stay
 * complete: the release checks fail if a key is missing.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const voice: Catalog = {
  'voice.title': 'Stem',
  'voice.description': 'Telefoonoproepe wat deur jou assistent beantwoord word, en na jou span deurgegee word wanneer ’n mens nodig is.',
  'voice.unavailable.title': 'Stem is nie beskikbaar nie',
  'voice.unavailable.body': 'Telefoonoproepe word tans nie op jou rekening aangebied nie.',
  'voice.state.applied': 'Wag vir goedkeuring',
  'voice.state.active': 'Aan',
  'voice.start.title': 'Laat jou assistent die telefoon antwoord',
  'voice.start.body': 'Bellers word dadelik beantwoord, enige tyd van die dag, uit dieselfde kennis wat jou assistent reeds in klets gebruik.',
  'voice.start.point.answer': 'Jou assistent beantwoord oproepe na jou nommer en soek antwoorde op terwyl dit praat.',
  'voice.start.point.transfer': 'Wanneer ’n beller ’n mens nodig het, word die oproep deurgegee aan iemand in jou span of na ’n nommer wat jy kies.',
  'voice.start.point.prepaid': 'Oproepe word per minuut uit jou krediete betaal. ’n Oproep kan nie sonder genoeg krediet begin nie, en jy stel die meeste wat op ’n dag bestee mag word.',
  'voice.start.point.privacy': 'Wat ’n beller vir die assistent sê, word buite Suid-Afrika verwerk. Jy kies wat bellers vertel word, en of oproepe opgeneem word.',
  'voice.start.accept': 'Ek aanvaar die stembepalings (weergawe {{version}})',
  'voice.start.readTerms': 'Lees die stembepalings',
  'voice.start.go': 'Vra vir stem',
  'voice.start.noTerms': 'Stem kan nog nie begin word nie. Die bepalings word nog voorberei.',
  'voice.start.doneApplied': 'Dankie. Ons laat weet jou wanneer stem vir jou aangeskakel is.',
  'voice.start.doneActive': 'Stem is vir jou rekening aangeskakel.',
  'voice.applied.title': 'Jy het vir stem gevra',
  'voice.applied.body': 'Ons hersien jou versoek. Niks word gehef en geen oproep word beantwoord totdat dit aangeskakel is nie.',
  'voice.suspended.title': 'Stem is op jou rekening opgeskort',
  'voice.suspended.noReason': 'Geen rede is aangeteken nie.',
  'voice.suspended.help': 'Oproepe word nie beantwoord terwyl dit opgeskort is nie. Kontak ondersteuning om daarna te laat kyk.',
  'voice.active.title': 'Stem is aan',
  'voice.active.body': 'Stel hoeveel op oproepe bestee mag word en of oproepe opgeneem word. Jy kan hierdie perke enige tyd verlaag.',
  'voice.active.outboundBlocked': 'Uitgaande oproepe is op jou rekening geblokkeer. {{reason}}',
  'voice.limits.title': 'Perke',
  'voice.limits.body': 'Perke beskerm jou as iets verkeerd loop. Wanneer een bereik word, stop uitgaande oproepe tot die volgende uur of dag. Een krediet is R1.',
  'voice.limits.daily': 'Meeste om op ’n dag aan oproepe te bestee (krediete)',
  'voice.limits.hourly': 'Meeste om in ’n uur aan oproepe te bestee (krediete)',
  'voice.limits.minutes': 'Langste oproep (minute)',
  'voice.limits.calls': 'Oproepe op dieselfde tyd',
  'voice.limits.upTo': 'Tot {{max}} krediete op jou rekening.',
  'voice.limits.upToMinutes': 'Tot {{max}} minute op jou rekening.',
  'voice.limits.upToCalls': 'Tot {{max}} op jou rekening.',
  'voice.limits.internationalOff': 'Oproepe na ander lande is op jou rekening afgeskakel.',
  'voice.limits.internationalOn': 'Oproepe na ander lande word op jou rekening toegelaat.',
  'voice.recording.title': 'Opname en privaatheid',
  'voice.recording.body': '’n Geskrewe rekord van elke oproep wat deur die assistent beantwoord is, word altyd gehou, sodat jou span kan sien wat gesê is. Om die klank op te neem, is jou keuse.',
  'voice.recording.enable': 'Neem oproepe op',
  'voice.recording.enableHint': 'Bellers word aan die begin van elke oproep ingelig. Opnames word veilig gestoor en uitgevee ná die tydperk wat jy stel.',
  'voice.recording.notice': 'Wat bellers vertel word',
  'voice.recording.noticeHint': 'Word aan die begin van elke opgeneemde oproep gesê, deur die assistent of as ’n opname wat albei kante hoor wanneer ’n persoon antwoord of uitbel. Hou dit by een sin.',
  'voice.recording.retention': 'Hou opnames vir (dae)',
  'voice.recording.retentionHint': 'Ná soveel dae word ’n opname finaal uitgevee.',
  'voice.recording.unavailable': 'Die opneem van die klank van oproepe is tans nie beskikbaar nie.',
  'voice.settings.saved': 'Steminstellings gestoor',
  'voice.settings.billingLink': 'Sien jou krediete en wat oproepe gekos het',
  'voice.tabs.numbers': 'Nommers',
  'voice.tabs.calls': 'Oproepe',
  'voice.tabs.settings': 'Perke en opname',
  'voice.numbers.title': 'Jou nommers',
  'voice.numbers.intro': 'Elke nommer hier word deur jou assistent beantwoord. Voeg ’n nommer by sodra sy telefoonverskaffer hieronder gekoppel is.',
  'voice.numbers.add': 'Voeg ’n nommer by',
  'voice.numbers.empty': 'Nog geen nommers nie',
  'voice.numbers.emptyHint': 'Voeg die nommer by wat jou kliënte bel. Dit word beantwoord sodra die assistent gereed is.',
  'voice.numbers.emptyNoProvider': 'Koppel eers jou telefoonverskaffer, en voeg dan die nommer by wat daardeur kom.',
  'voice.numbers.status.active': 'Aan',
  'voice.numbers.status.pending': 'Onderbreek',
  'voice.numbers.status.suspended': 'Opgeskort',
  'voice.numbers.assistant.ready': 'Die assistent is gereed om hierdie nommer te beantwoord.',
  'voice.numbers.assistant.pending': 'Die assistent word vir hierdie nommer opgestel. Dit neem gewoonlik minder as ’n minuut.',
  'voice.numbers.assistant.failed': 'Die assistent kon nog nie vir hierdie nommer opgestel word nie. Ons hou aan probeer.',
  'voice.numbers.assistant.paused': 'Oproepe na hierdie nommer word nie beantwoord terwyl dit onderbreek is nie.',
  'voice.numbers.pause': 'Onderbreek',
  'voice.numbers.resume': 'Skakel aan',
  'voice.numbers.removeTitle': 'Verwyder hierdie nommer?',
  'voice.numbers.removeBody': 'Oproepe na {{number}} sal nie meer hier beantwoord word nie. Die nommer bly by jou telefoonverskaffer.',
  'voice.numbers.removed': 'Nommer verwyder.',
  'voice.numbers.saved': 'Nommer gestoor.',
  'voice.numbers.form.new': 'Voeg ’n nommer by',
  'voice.numbers.form.edit': 'Hoe hierdie nommer beantwoord word',
  'voice.numbers.form.number': 'Telefoonnommer',
  'voice.numbers.form.numberHint': 'Die nommer wat jou kliënte bel, soos 087 123 4567 of +27 87 123 4567.',
  'voice.numbers.form.label': 'Naam',
  'voice.numbers.form.labelHint': 'Vir jou eie verwysing, soos Ontvangs of Verkope.',
  'voice.numbers.form.provider': 'Telefoonverskaffer',
  'voice.numbers.form.providerHint': 'Die verskaffer waardeur hierdie nommer kom. Oproepe van enige ander plek word nie beantwoord nie.',
  'voice.numbers.form.assistant': 'Assistent',
  'voice.numbers.form.assistantHint': 'Watter assistent antwoord. Dit gebruik dieselfde kennis as in jou kletse.',
  'voice.numbers.form.assistantDefault': 'Jou verstek-assistent',
  'voice.numbers.form.greeting': 'Groet',
  'voice.numbers.form.greetingHint': 'Die eerste ding wat ’n beller hoor. Hou dit by een sin.',
  'voice.numbers.form.greetingDefault': 'Hallo, dankie dat jy bel. Hoe kan ek help?',
  'voice.numbers.form.language': 'Taal',
  'voice.numbers.form.languageHint': 'Die taal wat die meeste bellers praat. Die assistent antwoord steeds in die beller se taal wanneer dit kan.',
  'voice.numbers.language.en': 'Engels',
  'voice.numbers.language.af': 'Afrikaans',
  'voice.numbers.language.zu': 'isiZulu',
  'voice.numbers.language.xh': 'isiXhosa',
  'voice.numbers.language.st': 'Sesotho',
  'voice.numbers.form.instructions': 'Enigiets anders wat die assistent op oproepe moet weet',
  'voice.numbers.form.instructionsHint': 'Byvoorbeeld: Ons sluit Saterdae om een. Neem ’n boodskap vir enigiets oor rekeninge.',
  'voice.providers.title': 'Telefoonverskaffers',
  'voice.providers.intro': 'Die maatskappy by wie jou nommer is. Koppel dit een keer en voeg soveel van sy nommers by as wat jy wil.',
  'voice.providers.add': 'Koppel ’n verskaffer',
  'voice.providers.empty': 'Geen verskaffer gekoppel nie',
  'voice.providers.emptyHint': 'Jy het die bedienernaam nodig wat jou verskaffer gegee het, en óf ’n gebruikersnaam en wagwoord óf die vermoë om oproepe na ’n adres aan te stuur.',
  'voice.providers.status.new': 'Word nagegaan',
  'voice.providers.status.ok': 'Gekoppel',
  'voice.providers.status.failing': 'Nie gekoppel nie',
  'voice.providers.modeLine.register': 'Ons meld aan by {{host}}',
  'voice.providers.modeLine.forward': '{{host}} stuur oproepe na ons',
  'voice.providers.checked': '{{when}} nagegaan',
  'voice.providers.removeTitle': 'Ontkoppel hierdie verskaffer?',
  'voice.providers.removeBody': '{{name}} sal ontkoppel word. Verwyder eers sy nommers as daar nog is.',
  'voice.providers.removed': 'Verskaffer ontkoppel.',
  'voice.providers.saved': 'Verskaffer gestoor. Ons gaan die verbinding nou na.',
  'voice.providers.form.new': 'Koppel ’n telefoonverskaffer',
  'voice.providers.form.edit': 'Verander ’n telefoonverskaffer',
  'voice.providers.form.name': 'Naam',
  'voice.providers.form.nameHint': 'Vir jou eie verwysing, soos die verskaffer se naam.',
  'voice.providers.form.mode': 'Hoe dit koppel',
  'voice.providers.form.mode.register': 'Ons meld aan',
  'voice.providers.form.mode.forward': 'Dit stuur oproepe na ons',
  'voice.providers.form.modeHint.register': 'Jou verskaffer het vir jou ’n bediener, ’n gebruikersnaam en ’n wagwoord gegee. Dit is die gewone manier.',
  'voice.providers.form.modeHint.forward': 'Jou verskaffer stuur oproepe aan na ’n adres wat jy gee. Gebruik dit wanneer daar geen gebruikersnaam en wagwoord is nie.',
  'voice.providers.form.host': 'Bediener',
  'voice.providers.form.hostHint': 'Die bedienernaam of adres van jou verskaffer, soos sip.example.co.za.',
  'voice.providers.form.port': 'Poort',
  'voice.providers.form.portHint': '5060 tensy jou verskaffer anders sê.',
  'voice.providers.form.transport': 'Verbindingstipe',
  'voice.providers.form.transportHint': 'UDP tensy jou verskaffer anders sê.',
  'voice.providers.form.username': 'Gebruikersnaam',
  'voice.providers.form.usernameHint': 'Van jou verskaffer. Dit is dikwels die rekening of die nommer self.',
  'voice.providers.form.password': 'Wagwoord',
  'voice.providers.form.passwordHint': 'Van jou verskaffer. Dit word geënkripteer gestoor en nooit weer gewys nie.',
  'voice.providers.form.passwordKeep': 'Laat leeg om die wagwoord wat reeds gestoor is te behou.',
  'voice.providers.form.sendTo': 'Vra jou verskaffer om oproepe na hierdie adres aan te stuur, met jou eie nommer in die plek:',
  'voice.providers.form.sendToNumber': 'jou-nommer',
  'voice.providers.form.sources': 'Adresse waarvandaan sy oproepe kom',
  'voice.providers.form.sourcesHint': 'Slegs as jou verskaffer oproepe van ander adresse as die bediener hierbo stuur. Een per reël, soos 196.10.20.30 of 196.10.20.0/24.',
  'voice.calls.title': 'Oproepe',
  'voice.calls.intro': 'Elke oproep na jou nommers, met wat gesê is en wat dit gekos het.',
  'voice.calls.empty': 'Nog geen oproepe nie',
  'voice.calls.emptyHint': 'Oproepe verskyn hier soos hulle gebeur.',
  'voice.calls.unknownCaller': 'Onbekende beller',
  'voice.calls.line': '{{when}} na {{number}}',
  'voice.calls.noCharge': 'Geen koste',
  'voice.calls.length.none': 'Nie beantwoord nie',
  'voice.calls.length.minutes': '{{minutes}} min',
  'voice.calls.length.minutesSeconds': '{{minutes}} min {{seconds}} s',
  'voice.calls.status.ringing': 'Lui',
  'voice.calls.status.active': 'Aan die gang',
  'voice.calls.status.ended': 'Beëindig',
  'voice.calls.ended.completed': 'Voltooi',
  'voice.calls.ended.caller_hung_up': 'Beller het neergesit',
  'voice.calls.ended.no_credit': 'Krediete het opgeraak',
  'voice.calls.ended.max_length': 'Langste oproeplengte bereik',
  'voice.calls.ended.kill_switch': 'Deur die diens beëindig',
  'voice.calls.ended.engine_lost': 'Verbinding verloor',
  'voice.calls.ended.failed': 'Kon nie beantwoord word nie',
  'voice.calls.ended.ended_by_platform': 'Deur ondersteuning beëindig',
  'voice.calls.showing': '{{from}} tot {{to}} van {{total}}',
  'voice.calls.newer': 'Nuwer',
  'voice.calls.older': 'Ouer',
  'voice.calls.detail.title': 'Oproep',
  'voice.calls.detail.from': 'Van',
  'voice.calls.detail.to': 'Na',
  'voice.calls.detail.when': 'Wanneer',
  'voice.calls.detail.ended': 'Hoe dit geëindig het',
  'voice.calls.detail.length': 'Gehef vir',
  'voice.calls.detail.cost': 'Koste',
  'voice.calls.detail.costHint': 'Oproepe word per volle minuut gehef. ’n Oproep wat nie beantwoord kon word nie, kos niks.',
  'voice.calls.detail.transcript': 'Wat gesê is',
  'voice.calls.detail.noTranscript': 'Niks is op hierdie oproep gesê nie.',
  'voice.calls.speaker.caller': 'Beller',
  'voice.calls.speaker.assistant': 'Assistent',
  'voice.calls.speaker.tool': 'Assistent het gedoen',
  'voice.calls.speaker.system': 'Nota',
  'voice.tabs.team': 'Span',
  'voice.calls.passedTo': 'Deurgegee na {{who}}',
  'voice.calls.pass.label': 'Gee hierdie oproep deur na',
  'voice.calls.pass.hint': 'Hulle foon lui terwyl die beller by die assistent bly. As niemand antwoord nie, verander niks.',
  'voice.calls.pass.choose': 'Kies ’n persoon of afdeling',
  'voice.calls.pass.go': 'Gee die oproep deur',
  'voice.calls.pass.done': 'Die oproep is nou by {{who}}.',
  'voice.calls.ended.no_answer': 'Geen antwoord',
  'voice.lines.title': 'Lyne',
  'voice.lines.intro': '’n Lyn is ’n persoon wat oproepe neem: op ’n foon of toepassing wat aanmeld, of op ’n selfoon of landlyn waarheen die oproep aangestuur word. Kollegas skakel mekaar met die lyn se nommer.',
  'voice.lines.add': 'Voeg ’n lyn by',
  'voice.lines.empty': 'Nog geen lyne nie',
  'voice.lines.emptyHint': 'Voeg ’n lyn by vir elke persoon na wie die assistent ’n beller moet kan deurgee.',
  'voice.lines.state.signedIn': 'Aangemeld',
  'voice.lines.state.signedOut': 'Nie aangemeld nie',
  'voice.lines.state.forward': 'Aanstuur',
  'voice.lines.state.off': 'Af',
  'voice.lines.state.dialOut': 'Kan uitbel',
  'voice.lines.forwardLine': 'Oproepe word aangestuur na {{number}}',
  'voice.lines.phoneLineIn': '’n Foon is aangemeld en kan oproepe neem.',
  'voice.lines.phoneLineOut': 'Geen foon is aangemeld nie, dus kan hierdie lyn nie lui nie.',
  'voice.lines.noTransfers': 'die assistent gee nie bellers hierheen deur nie',
  'voice.lines.replace': 'Nuwe aanmeldbesonderhede',
  'voice.lines.replaceTitle': 'Vervang die aanmeldbesonderhede?',
  'voice.lines.replaceBody': 'Die foon wat as {{name}} aangemeld is, word dadelik afgemeld en sal die nuwe besonderhede nodig hê. Gebruik dit wanneer ’n foon verlore is of ’n persoon weggaan.',
  'voice.lines.replaceConfirm': 'Vervang hulle',
  'voice.lines.removeTitle': 'Verwyder hierdie lyn?',
  'voice.lines.removeBody': '{{name}} ({{number}}) sal nie meer oproepe neem nie, en sy foon word afgemeld.',
  'voice.lines.removed': 'Lyn verwyder.',
  'voice.lines.saved': 'Lyn gestoor.',
  'voice.lines.blocked.title': 'Uitgaande oproepe is gestop',
  'voice.lines.blocked.help': 'Inkomende oproepe werk steeds. Kontak ondersteuning om uitgaande oproepe weer aan te skakel.',
  'voice.lines.form.new': 'Voeg ’n lyn by',
  'voice.lines.form.edit': 'Verander ’n lyn',
  'voice.lines.form.kind': 'Wat die oproep neem',
  'voice.lines.form.kind.phone': '’n Foon of toepassing',
  'voice.lines.form.kind.forward': '’n Selfoon of landlyn',
  'voice.lines.form.kindHint.phone': '’n Lessenaarfoon of ’n toepassing wat aanmeld met besonderhede wat ons vir jou gee. Niks ekstra word vir sy kant van ’n oproep gehef nie.',
  'voice.lines.form.kindHint.forward': 'Die oproep word deur jou telefoonverskaffer na ’n buitenommer aangestuur, en as ’n tweede lyn gehef.',
  'voice.lines.form.number': 'Lynnommer',
  'voice.lines.form.numberHint': 'Drie of vier syfers, soos 101.',
  'voice.lines.form.name': 'Naam',
  'voice.lines.form.nameHint': 'Die persoon se naam. Die assistent gebruik dit wanneer ’n beller na hulle vra.',
  'voice.lines.form.forwardTo': 'Stuur aan na',
  'voice.lines.form.forwardToHint': '’n Suid-Afrikaanse selfoon- of landlynnommer, soos 082 555 0100.',
  'voice.lines.form.transfers': 'Die assistent mag bellers na hierdie lyn deurgee',
  'voice.lines.form.transfersHint': 'Skakel af vir ’n lyn wat net deur ’n afdeling bereik moet word.',
  'voice.lines.form.dialOut': 'Hierdie lyn mag buitenommers bel',
  'voice.lines.form.dialOutHint': 'Af tensy jy dit aanskakel. Oproepe gaan deur jou telefoonverskaffer uit en wys jou nommer.',
  'voice.lines.form.dialOutUnavailable': 'Uitgaande oproepe is tans nie beskikbaar nie.',
  'voice.lines.form.active': 'Lyn is aan',
  'voice.lines.form.activeHint': 'Skakel af om te keer dat hierdie lyn lui sonder om dit te verwyder.',
  'voice.lines.details.title': 'Aanmeldbesonderhede vir hierdie foon',
  'voice.lines.details.once': 'Kopieer dit nou. Die wagwoord word net hierdie een keer gewys en kan nie weer gewys word nie; as dit verlore is, kies Nuwe aanmeldbesonderhede.',
  'voice.lines.details.server': 'Bediener',
  'voice.lines.details.serverLater': 'Ondersteuning sal die bedieneradres vir jou gee',
  'voice.lines.details.port': 'Poort',
  'voice.lines.details.transport': 'Verbindingstipe',
  'voice.lines.details.sound': 'Klank',
  'voice.lines.details.soundValue': 'Geënkripteer (SRTP), verpligtend',
  'voice.lines.details.username': 'Gebruikersnaam',
  'voice.lines.details.password': 'Wagwoord',
  'voice.lines.details.help': 'Voer dit in die foon of toepassing in as ’n SIP-rekening. Dit moet TLS en geënkripteerde klank gebruik, anders sal dit nie koppel nie.',
  'voice.lines.details.copy': 'Kopieer',
  'voice.lines.details.copied': 'Gekopieer.',
  'voice.lines.details.done': 'Ek het dit gestoor',
  'voice.groups.title': 'Afdelings',
  'voice.groups.intro': '’n Afdeling is verskeie lyne wat om dieselfde rede lui, soos Verkope. Die assistent gee ’n beller na ’n afdeling deur met sy naam.',
  'voice.groups.add': 'Voeg ’n afdeling by',
  'voice.groups.empty': 'Nog geen afdelings nie',
  'voice.groups.emptyHint': 'Voeg ’n afdeling by wanneer meer as een persoon dieselfde soort oproep kan neem.',
  'voice.groups.noLines': 'Nog geen lyne nie, dus kan die assistent nie ’n beller hierheen deurgee nie.',
  'voice.groups.strategyLine.together': 'Almal lui gelyk vir {{seconds}} sekondes',
  'voice.groups.strategyLine.in_order': 'Lui een na die ander, {{seconds}} sekondes altesaam',
  'voice.groups.purposeLine': 'Vir: {{purpose}}',
  'voice.groups.removeTitle': 'Verwyder hierdie afdeling?',
  'voice.groups.removeBody': '{{name}} sal verwyder word. Sy lyne bly.',
  'voice.groups.removed': 'Afdeling verwyder.',
  'voice.groups.saved': 'Afdeling gestoor.',
  'voice.groups.form.new': 'Voeg ’n afdeling by',
  'voice.groups.form.edit': 'Verander ’n afdeling',
  'voice.groups.form.name': 'Naam',
  'voice.groups.form.nameHint': 'Waarna ’n beller sou vra, soos Verkope of Rekeninge.',
  'voice.groups.form.purpose': 'Waarvoor dit is',
  'voice.groups.form.purposeHint': 'Sê vir die assistent wanneer om ’n beller hierheen deur te gee, soos: nuwe bestellings en kwotasies.',
  'voice.groups.form.strategy': 'Hoe die lyne lui',
  'voice.groups.form.strategy.together': 'Almal gelyk',
  'voice.groups.form.strategy.in_order': 'Een na die ander',
  'voice.groups.form.strategyHint.together': 'Elke lyn lui; die eerste wat antwoord, neem die oproep.',
  'voice.groups.form.strategyHint.in_order': 'Elke lyn lui op sy beurt, in die volgorde waarin jy hulle merk.',
  'voice.groups.form.seconds': 'Lui vir (sekondes)',
  'voice.groups.form.secondsHint': 'Tussen 5 en 120. Daarna gaan die beller terug na die assistent.',
  'voice.groups.form.lines': 'Lyne in hierdie afdeling',
  'voice.groups.form.linesHint': '’n Foon wat nie aangemeld is nie, word oorgeslaan.',
  'voice.numbers.form.answerWith': 'Wie eerste antwoord',
  'voice.numbers.form.answerWith.assistant': 'Die assistent',
  'voice.numbers.form.answerWith.department': '’n Afdeling',
  'voice.numbers.form.answerWithHint.assistant': 'Die assistent antwoord en gee die beller deur wanneer ’n mens nodig is.',
  'voice.numbers.form.answerWithHint.department': 'Die afdeling lui eerste. As niemand antwoord nie, antwoord die assistent.',
  'voice.numbers.form.department': 'Afdeling',
  'voice.numbers.form.departmentHint': 'Die afdeling wat eerste lui.',
  'voice.numbers.form.departmentChoose': 'Kies ’n afdeling',
  'voice.numbers.fromUs': 'Van ons',
  'voice.numbers.rental.next': 'Volgende huur op {{when}}',
  'voice.numbers.rental.overdue': 'Die huur vir hierdie nommer is nie betaal nie. Laai op om dit te behou.',
  'voice.apply.title': 'Kry ’n nommer van ons',
  'voice.apply.intro': 'Geen telefoonnommer van jou eie nie? Doen aansoek vir een. Ons gaan jou besonderhede na, ken ’n nommer toe, en jou assistent beantwoord dit.',
  'voice.apply.go': 'Doen aansoek vir ’n nommer',
  'voice.apply.cost.setup': 'Registrasie, een keer',
  'voice.apply.cost.monthly': 'Huur, elke maand',
  'voice.apply.cost.note': 'Albei word betaal uit krediete wat jy gekoop het. Krediete wat saam met ’n plan gekom het, kan dit nie betaal nie.',
  'voice.apply.cost.topup': 'Koop krediete',
  'voice.apply.notYet': 'Daar kan nog nie vir nommers aansoek gedoen word nie.',
  'voice.apply.kind.national': '’n Nasionale nommer (087)',
  'voice.apply.kind.geographic': '’n Plaaslike nommer (010, 011, 021 ensovoorts)',
  'voice.apply.status.submitted': 'Word nagegaan',
  'voice.apply.status.approved': 'Goedgekeur',
  'voice.apply.status.rejected': 'Nie goedgekeur nie',
  'voice.apply.status.withdrawn': 'Teruggetrek',
  'voice.apply.line.submitted': 'Aansoek gedoen op {{when}}. {{fee}} word gehou totdat ons besluit; niks is gehef nie.',
  'voice.apply.line.approved': 'Aansoek gedoen op {{when}}. {{fee}} is vir registrasie gehef.',
  'voice.apply.line.rejected': 'Aansoek gedoen op {{when}}. Niks is gehef nie.',
  'voice.apply.line.withdrawn': 'Aansoek gedoen op {{when}} en teruggetrek. Niks is gehef nie.',
  'voice.apply.withdraw': 'Trek terug',
  'voice.apply.withdrawTitle': 'Trek hierdie aansoek terug?',
  'voice.apply.withdrawBody': 'Die {{fee}} wat daarvoor gehou word, is weer joune om te bestee.',
  'voice.apply.withdrawn': 'Aansoek teruggetrek. Die krediete wat gehou is, is weer joune om te bestee.',
  'voice.apply.sent': 'Aansoek gestuur. Ons sal dit nagaan en jou laat weet.',
  'voice.apply.form.title': 'Doen aansoek vir ’n nommer',
  'voice.apply.form.held': '{{fee}} word gehou wanneer jy aansoek doen en net gehef as ons goedkeur. Daarna is die huur {{monthly}} per maand. As ons nie goedkeur nie, of jy terugtrek, word niks gehef nie.',
  'voice.apply.form.kind': 'Soort nommer',
  'voice.apply.form.kindHint.national': 'Nie aan ’n dorp gekoppel nie. ’n Goeie keuse vir ’n besigheid wat die hele land bedien.',
  'voice.apply.form.kindHint.geographic': 'Dra ’n dorp se skakelkode. Ons sal vra watter dorp wanneer ons dit nagaan.',
  'voice.apply.form.business': 'Besigheidsnaam',
  'voice.apply.form.businessHint': 'Die geregistreerde naam waarvoor die nommer is.',
  'voice.apply.form.registration': 'Registrasienommer',
  'voice.apply.form.registrationHint': 'Maatskappy- of ID-nommer, as jy een het.',
  'voice.apply.form.contact': 'Kontakpersoon',
  'voice.apply.form.contactHint': 'Met wie ons oor hierdie aansoek praat.',
  'voice.apply.form.phone': 'Kontaktelefoon',
  'voice.apply.form.phoneHint': '’n Nommer waarop ons daardie persoon kan bereik.',
  'voice.apply.form.address': 'Besigheidsadres',
  'voice.apply.form.addressHint': 'Straatadres van die besigheid.',
  'voice.apply.form.purpose': 'Waarvoor die nommer is',
  'voice.apply.form.purposeHint': 'Byvoorbeeld: ons winkel se hooflyn vir kliëntevrae.',
  'voice.apply.form.agree': 'Ek bevestig dat hierdie besonderhede korrek is, en stem in dat {{fee}} nou gehou word en gehef word as die aansoek goedgekeur word.',
  'voice.apply.form.send': 'Stuur aansoek',
  'voice.calls.recorded': 'Opgeneem',
  'voice.calls.quality.good': 'Duidelike lyn',
  'voice.calls.quality.fair': 'Lyn effens onduidelik',
  'voice.calls.quality.poor': 'Swak lyn',
  'voice.calls.detail.quality': 'Lyn',
  'voice.calls.recording.listen': 'Luister na die opname',
  'voice.calls.recording.listenHint': 'Elke keer wat ’n opname oopgemaak word, word dit aangeteken, met wie dit oopgemaak het.',
  'voice.calls.recording.keptUntil': 'Hierdie opname word tot {{when}} gehou en dan uitgevee.',
  'voice.calls.recording.recording': 'Hierdie oproep word opgeneem.',
  'voice.calls.recording.waiting': 'Die opname word gestoor. Dit sal oor ’n oomblik hier wees.',
  'voice.calls.recording.failed': 'Die opname van hierdie oproep kon nie gestoor word nie.',
  'voice.calls.recording.deleted': 'Die opname van hierdie oproep is uitgevee. Wat gesê is, is steeds hieronder.',
  'voice.apply.form.documents': 'Dokumente',
  'voice.apply.form.documentsHint': 'Die wet (RICA) vereis dat ons weet vir wie ’n nommer is. Heg elke dokument as ’n PDF of ’n foto aan; dit gaan na jou lêers en word net oopgemaak deur die persoon wat jou aansoek nagaan.',
  'voice.apply.form.attach': 'Heg aan',
  'voice.apply.form.replace': 'Vervang',
  'voice.apply.form.attachFor': 'Heg {{name}} aan',
  'voice.apply.form.attached': 'Aangeheg: {{name}}',
  'voice.apply.form.notAttached': 'Nog nie aangeheg nie',
  'voice.apply.form.uploadFailed': 'Die lêer kon nie opgelaai word nie.',
  'voice.recording.noticeReady': 'Jou kennisgewing is gereed as spraak. Dit word gespeel voordat ’n oproep wat ’n persoon antwoord of maak, opgeneem word.',
  'voice.recording.noticeUnavailable': 'Jou kennisgewing kon nog nie in spraak omgesit word nie, so oproepe wat ’n persoon antwoord of maak, word nie opgeneem nie. Oproepe wat die assistent antwoord, word steeds opgeneem. Stoor dit later weer.',
  'voice.numbers.whatsapp': 'WhatsApp',
  'voice.numbers.whatsappHint': 'Neem oproepe uit WhatsApp. Skakel hulle op die Nommers-bladsy af.',
  'voice.calls.whatsapp': 'WhatsApp'
};

export default voice;
